import { Trans } from "react-i18next";
import i18next from "src/i18n/config";

import { GroupEvents } from "@dashboard/devices/types";
import { getEventColors } from "@dashboard/overview/components/EventChartCard/EventChartCard.utils";
import { Theme } from "@mui/material";

import { createGroupLink, extractPlainText, getActorTitle } from "./shared/eventHelpers";
import type { EventContentResult, EventContext, RendererProps } from "./shared/eventTypes";
import { renderUnknownEventContent } from "./unknown";

type GroupCreatedEvent = Extract<GroupEvents, { eventTypeName: "GroupEvent.Created" }>;
const renderGroupCreated = (props: RendererProps<GroupCreatedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { group } = context;
    const eventColors = getEventColors(theme);
    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));

    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.groupCreatedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.groupCreatedDescription"
                values={{
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                }}
                components={{
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type GroupRenamedEvent = Extract<GroupEvents, { eventTypeName: "GroupEvent.Renamed" }>;
const renderGroupRenamed = (props: RendererProps<GroupRenamedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const eventColors = getEventColors(theme);
    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));

    const newNameLabel = extractPlainText(event.data?.name ?? context.group?.name ?? i18next.t("common:status.unknown"));

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.groupRenamedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.groupRenamedDescription"
                values={{
                    newName: newNameLabel,
                    actorTitle: actorLabel,
                }}
                components={{
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type GroupDeletedEvent = Extract<GroupEvents, { eventTypeName: "GroupEvent.Deleted" }>;
const renderGroupDeleted = (props: RendererProps<GroupDeletedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { group } = context;
    const eventColors = getEventColors(theme);
    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));

    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.groupDeletedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.groupDeletedDescription"
                values={{
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                }}
                components={{
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

export const renderGroupEventContent = (event: GroupEvents, context: EventContext, theme: Theme): EventContentResult | null => {
    switch (event.eventTypeName) {
        case "GroupEvent.Created":
            return renderGroupCreated({ event, context, theme });
        case "GroupEvent.Renamed":
            return renderGroupRenamed({ event, context, theme });
        case "GroupEvent.Deleted":
            return renderGroupDeleted({ event, context, theme });
        default:
            return renderUnknownEventContent({ event, context, theme });
    }
};
